import { type NumberString, type Crop, type TopRightBottomLeft, type Format } from './types.ts';

/** Fastly Image Optimization query parameters.
 *  @see http://developer.fastly.com/reference/io/
 */
type IOParams = {
    /** @see http://developer.fastly.com/reference/io/auto/ */
    auto?: 'webp';
    /** @see http://developer.fastly.comreference/io/bg-color/ */
    'bg-color'?: string | `${number},${number},${number}`;
    /** @see http://developer.fastly.com/reference/io/blur/ */
    blur?: `${number}p` | `${number}%`;
    /** @see http://developer.fastly.com/reference/io/brightness/ */
    brightness?: NumberString;
    /** @see http://developer.fastly.com/reference/io/canvas/ */
    canvas?: string; // TODO
    /** @see http://developer.fastly.com/reference/io/contrast/ */
    contrast?: NumberString;
    /** @see http://developer.fastly.com/reference/io/crop/ */
    crop?: Crop;
    /** @see http://developer.fastly.com/reference/io/disable/ */
    disable?: 'upscale';
    /** @see http://developer.fastly.com/reference/io/dpr/ */
    dpr?: `${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10}`;
    /** @see http://developer.fastly.com/reference/io/enable/ */
    enable?: 'upscale';
    /** @see http://developer.fastly.com/reference/io/fit/ */
    fit?: 'bounds' | 'cover' | 'crop';
    /** @see http://developer.fastly.com/reference/io/format/ */
    format?: Format;
    /** @see http://developer.fastly.com/reference/io/frame/ */
    frame?: '1';
    /** @see http://developer.fastly.com/reference/io/height/ */
    height?: NumberString | `${number}p`;
    /** @see http://developer.fastly.com/reference/io/level/ */
    level?: `1.${'0' | '1' | '2' | '3'}` | `${'2' | '3' | '4' | '5' | '6'}.${'0' | '1' | '2'}`;
    /** @see http://developer.fastly.com/reference/io/optimize/ */
    optimize?: 'low' | 'medium' | 'high';
    /** @see http://developer.fastly.com/reference/io/orient/ */
    orient?: 'r' | 'l' | 'h' | 'v' | 'hv' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8';
    /** @see http://developer.fastly.com/reference/io/pad/ */
    pad?: TopRightBottomLeft;
    /** @see http://developer.fastly.com/reference/io/precrop/ */
    precrop?: Crop;
    /** @see http://developer.fastly.com/reference/io/profile/ */
    profile?: 'baseline' | 'main' | 'high';
    /** @see http://developer.fastly.com/reference/io/quality/ */
    quality?: NumberString | `${number},${number}`;
    /** @see http://developer.fastly.comreference/io/resize-filter/ */
    'resize-filter'?: 'nearest' | 'bilinear' | 'bicubic' | 'lanczos2' | 'lanczos3';
    /** @see http://developer.fastly.com/reference/io/saturation/ */
    saturation?: NumberString;
    /** @see http://developer.fastly.com/reference/io/sharpen/ */
    sharpen?: `a${number},r${number},t${number}`;
    /** @see http://developer.fastly.com/reference/io/trim/ */
    trim?: TopRightBottomLeft;
    /** @see http://developer.fastly.com/reference/io/width/ */
    width?: NumberString | `${number}p`;
};

// using IOParams for the argument type enables typechecking as a level of
// validation on Fastly image optimization string construction.
// bonus: property-value pairs have JSDoc style comments with links to the
// Fastly API.

type MergeIOParams = IOParams & { toQuery(): string };

export function ioParams(...args: IOParams[]): MergeIOParams {
    const merged: MergeIOParams = Object.assign({}, ...args);
    // define internal toString
    const paramsToQueryString = (): string =>
        '?' +
        Object.entries(merged)
            .map(entry => entry.join('='))
            .join('&');

    Object.defineProperty(merged, 'toQuery', {
        writable: false,
        value: paramsToQueryString,
    });

    return merged;
}

type IOParamsSubset<T extends IOParams> = {
    [K in keyof T]: Exclude<T[K], undefined>;
};
/** returns partial record without optional properties. useful for
 * URLSearchParams, which requires `Record<string, string>` */
export const ioParamsSubset = <T extends IOParams>(
    params: IOParamsSubset<T>
): { [K in keyof T]: Exclude<T[K], undefined> } => params;
